import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, Line } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import { UsageType, UsageTypeValue } from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { useMonthlyUsageData } from '../Charts/hooks/useMonthlyUsageData';
import { BarChart } from 'src/common/components/Graphs/common/Graphs/BarChart';
import { EmptyLineOrBarChart } from 'src/common/components/Graphs/common/Graphs/EmptyLineOrBarChart';
import { useDailyUsageData } from '../Charts/hooks/useDailyUsageData';
import { ChartType, ChartTypeValue } from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { AreaChart } from 'src/common/components/Graphs/common/Graphs/AreaChart';
import { usageTooltipValueFormatter } from 'src/common/utils/mapUsageDTOtoAreaChartData';
import { dashboardQueries } from '../Charts/queries/dashboardQueries';
import { useHasUsageChartData } from '../hooks/useHasUsageChartData';
import CardContent from '@mui/material/CardContent';
import {
  StyledChartItem,
  StyledUsageChartsContainer,
  UsageCharts,
} from 'src/common/components/Graphs/common/Graphs/UsageCharts';
import Typography from '@mui/material/Typography';
import { WidgetRoot } from './Widget';
import { pxToRem } from 'src/common/utils/fonts';
import { getDefaultBarChartLabels } from 'src/common/components/Graphs/common/Graphs/utils/getDefaultBarChartLabels';
import { getDefaultAreaChartXaxisLabels } from 'src/common/components/Graphs/common/Graphs/utils/getDefaultAreaChartXAxisLabels';
import { useChartDataLastUpdated } from 'src/dashboard/hooks/useChartDataLastUpdated';

const StyledUsageCardContent = styled(CardContent)(({ theme }) => ({
  height: '100%',
  padding: `${theme.spacing(16)} ${theme.spacing(20)} ${theme.spacing(20)} ${theme.spacing(
    20,
  )} !important`,
}));

const usageChartTooltipLabelOrder = {
  Limit: 0,
  Total: 1,
  Upload: 2,
  Donwload: 3,
  MO: 2,
  MT: 3,
};

export const UsageSectionWidget = ({ accountRef }: { accountRef: string | null }) => {
  const { t } = useTranslation();

  if (!accountRef) {
    return (
      <WidgetRoot>
        <StyledUsageCardContent>
          <Typography
            variant="h3"
            color="text.secondary"
            pl={10}
            sx={{ height: pxToRem(32), mb: 16 }}
          >
            {t('dashboard.cumulativeUsageChart.title')}
          </Typography>
          <StyledUsageChartsContainer>
            <StyledChartItem>
              <EmptyLineOrBarChart
                as={Bar}
                emptyChartText={t('dashboard.placeholders.selectAccount')}
                xAxisLabels={getDefaultBarChartLabels(true)}
              />
            </StyledChartItem>
            <StyledChartItem>
              <EmptyLineOrBarChart
                as={Line}
                emptyChartText={t('dashboard.placeholders.selectAccount')}
                xAxisLabels={getDefaultAreaChartXaxisLabels(true)}
              />
            </StyledChartItem>
          </StyledUsageChartsContainer>
        </StyledUsageCardContent>
      </WidgetRoot>
    );
  }

  return <UsageSectionContent accountRef={accountRef} />;
};

const MothlyUsageBarChart = ({
  usageType,
  accountRef,
}: {
  accountRef: string;
  usageType: UsageTypeValue;
}) => {
  const monthlyUsageData = useMonthlyUsageData({ accountRef, usageType });

  const monthlyUsageXAxisLabels = monthlyUsageData.datasets.length
    ? monthlyUsageData.xAxisLabels
    : getDefaultBarChartLabels(true);

  return (
    <BarChart
      xAxisLabels={monthlyUsageXAxisLabels}
      datasets={monthlyUsageData.datasets}
      yAxisLabel={monthlyUsageData.yAxisLabel}
      tooltipLabelPriority={usageChartTooltipLabelOrder}
      tooltipValueFormatter={(value: number, unit?: string) =>
        usageTooltipValueFormatter(value, usageType, unit)
      }
    />
  );
};

const DailyUsageAreaChart = ({
  usageType,
  accountRef,
  chartType,
}: {
  accountRef: string;
  usageType: UsageTypeValue;
  chartType: ChartTypeValue;
}) => {
  const dailyUsageData = useDailyUsageData({ accountRef, usageType, chartType });

  return (
    <AreaChart
      datasets={dailyUsageData.datasets}
      limit={dailyUsageData.limit}
      yAxisLabel={dailyUsageData.yAxisLabel}
      xAxisLabels={dailyUsageData.xAxisLabels}
      realDataEndIndex={dailyUsageData.realDataEndIndex}
      tooltipLabelPriority={usageChartTooltipLabelOrder}
      tooltipValueFormatter={(value: number, unit?: string) =>
        usageTooltipValueFormatter(value, usageType, unit)
      }
    />
  );
};

const UsageSectionContent = ({ accountRef }: { accountRef: string }) => {
  const { t } = useTranslation();
  const [usageType, setUsageType] = useState<UsageTypeValue>(UsageType.Data);
  const [chartType, setChartType] = useState<ChartTypeValue>(ChartType.Cumulative);

  const hasMonthlyData = useHasUsageChartData(
    dashboardQueries.monthlyUsage({ accountRef, usageType }),
  );

  const hasDailyData = useHasUsageChartData(
    dashboardQueries.dailyUsage({ accountRef, chartType, usageType }),
  );

  const lastUpdatedDate = useChartDataLastUpdated(
    dashboardQueries.dailyUsage({ accountRef, chartType, usageType }),
  );

  const shouldDisplayLegend = hasMonthlyData || hasDailyData;

  const toggleType = () => {
    setChartType((p) =>
      p === ChartType.Cumulative ? ChartType.NonCumulative : ChartType.Cumulative,
    );
  };

  return (
    <WidgetRoot sx={{ height: '100%', boxShadow: 'none' }}>
      <StyledUsageCardContent>
        <UsageCharts
          headerUsageTitle={t('dashboard.cumulativeUsageChart.title')}
          usageType={usageType}
          setUsageType={setUsageType}
          chartType={chartType}
          toggleChartType={toggleType}
          displayMonthlyChart={true}
          monthlyChart={<MothlyUsageBarChart accountRef={accountRef} usageType={usageType} />}
          displayDailyChart={true}
          dailyChart={
            <DailyUsageAreaChart
              accountRef={accountRef}
              usageType={usageType}
              chartType={chartType}
            />
          }
          shouldDisplayLegend={shouldDisplayLegend}
          lastUpdated={lastUpdatedDate}
        />
      </StyledUsageCardContent>
    </WidgetRoot>
  );
};
